// userContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db, googleProvider } from './firebase';
import api from './utils/api';
import { clearCSRFToken } from './utils/csrfUtils';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  setPersistence,
  inMemoryPersistence
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot
} from 'firebase/firestore';

const UserContext = createContext(null);

// Export the custom hook
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Export the provider component
export function UserProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [userStatus, setUserStatus] = useState('unregistered'); // 'unregistered', 'pending_verification', 'pending_profile', 'verified'
  const [isLoading, setIsLoading] = useState(true); // Start loading initially
  const [statusMessage, setStatusMessage] = useState('');
  const [showResendButton, setShowResendButton] = useState(false);
  const [showResetButton, setShowResetButton] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState({ count: 0, lastAttempt: null });

  const signOutUser = async () => {
    try {
      setIsLoading(true);
      await signOut(auth);
      clearCSRFToken(); // Clear CSRF token on logout
      setCurrentUser(null);
      setUserDoc(null);
      setUserStatus('unregistered'); // Reset status on sign out
      setStatusMessage('');
      setShowResendButton(false);
      setShowResetButton(false);
      setIsLoading(false);
    } catch (error) {
      console.error('Error signing out:', error);
      setIsLoading(false);
      throw error;
    }
  };
  
  const resendVerificationEmail = async () => {
    setIsLoading(true);
    setStatusMessage('');
    
    try {
      if (!auth.currentUser) {
        setIsLoading(false);
        setStatusMessage('No user is currently signed in');
        return { status: 'error', message: 'No user is currently signed in' };
      }

      // Refresh the user's auth state to get the latest emailVerified status
      try {
        await auth.currentUser.reload();
      } catch (reloadError) {
        console.warn('Could not reload user auth state:', reloadError);
      }

      // Check if user is already verified after reload
      if (auth.currentUser.emailVerified) {
        setIsLoading(false);
        setStatusMessage('Your email is already verified');
        return { status: 'already_verified' };
      }

      // Check cooldown period (prevent spam - 1 minute minimum between requests)
      const lastResendTime = localStorage.getItem('lastResendTime');
      const now = Date.now();
      const oneMinute = 60 * 1000;
      
      if (lastResendTime && (now - parseInt(lastResendTime)) < oneMinute) {
        const remainingTime = Math.ceil((oneMinute - (now - parseInt(lastResendTime))) / 1000);
        setIsLoading(false);
        setStatusMessage(`Please wait ${remainingTime} seconds before requesting another email`);
        return { status: 'cooldown', remainingTime };
      }

      // Refresh the user's ID token before sending verification email
      try {
        await auth.currentUser.getIdToken(true); // Force refresh
      } catch (tokenError) {
        console.warn('Could not refresh auth token:', tokenError);
      }

      // Use the correct continue URL format
      const actionCodeSettings = {
        url: window.location.origin,
        handleCodeInApp: true
      };

      await sendEmailVerification(auth.currentUser, actionCodeSettings);
      
      // Store timestamp for cooldown
      localStorage.setItem('lastResendTime', now.toString());
      
      setIsLoading(false);
      setStatusMessage('Verification email sent! Please check your inbox and spam folder.');
      
      return { status: 'sent' };
    } catch (error) {
      setIsLoading(false);
      console.error('Error resending verification:', error);
      
      // Handle specific Firebase errors
      if (error.code === 'auth/too-many-requests') {
        setStatusMessage('Too many requests. Please try again later.');
      } else if (error.code === 'auth/user-token-expired') {
        setStatusMessage('Session expired. Please sign in again.');
      } else if (error.code === 'auth/network-request-failed') {
        setStatusMessage('Network error. Please check your connection and try again.');
      } else if (error.code === 'auth/invalid-continue-uri') {
        setStatusMessage('Configuration error. Please contact support.');
      } else {
        setStatusMessage('Failed to send verification email. Please try again.');
      }
      
      return { status: 'error', message: error.message };
    }
  };
  const signInWithGoogle = async () => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    setStatusMessage('');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const response = await api.post('/api/checkUserStatus', { email: user.email });
      const data = await response.json();

      setCurrentUser(user);

      if (data.userState === 'verified' && data.docData?.fullData === true) {
        setUserDoc(data.docData);
        setUserStatus('verified');
        setIsLoading(false);
        return { status: 'verified', user, userDoc: data.docData };
      } else if (data.userState === 'authNoDoc') {
        // New Google user - create pendingUsers document
        try {
          const displayName = user.displayName || '';
          const nameParts = displayName.split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          await setDoc(doc(db, 'pendingUsers', user.uid), {
            firstName,
            lastName,
            email: user.email.toLowerCase(),
            phone: '',
            grade: '',
            userType: '',
            subjects: [],
            createdAt: new Date(),
            emailVerified: user.emailVerified,
            fullData: false
          });

          setUserDoc({
            firstName,
            lastName,
            email: user.email,
            phone: '',
            grade: '',
            userType: '',
            subjects: [],
            emailVerified: user.emailVerified,
            fullData: false
          });
          setUserStatus('pending_profile');
          setStatusMessage('Please complete your profile information.');
          setIsLoading(false);
          return { status: 'pending_profile', user, userDoc: null };
        } catch (createError) {
          console.error('Error creating pendingUsers document:', createError);
          setUserDoc(null);
          setUserStatus('pending_profile');
          setStatusMessage('Please complete your profile information.');
          setIsLoading(false);
          return { status: 'pending_profile', user, userDoc: null };
        }      } else if (data.userState === 'pending' || data.docData?.fullData === false) {
        setUserDoc(data.docData);
        // For Google sign-in, check if user already has complete profile data
        if (data.docData?.fullData === true && user.emailVerified) {
          setUserStatus('verified');
          setIsLoading(false);
          return { status: 'verified', user, userDoc: data.docData };
        } else {
          setUserStatus(user.emailVerified ? 'pending_profile' : 'pending_verification');
          setStatusMessage(user.emailVerified ? 'Please complete your profile.' : 'Please check your email to verify your account.');
          setShowResendButton(!user.emailVerified);
          setIsLoading(false);
          return { status: user.emailVerified ? 'pending_profile' : 'pending_verification', user, userDoc: data.docData };
        }
      } else {
        setUserDoc(null);
        setUserStatus('pending_profile');
        setStatusMessage('Please complete your profile information.');
        setIsLoading(false);
        return { status: 'pending_profile', user, userDoc: null };
      }
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const signInWithEmailPassword = async (email, password) => {
    setIsLoading(true);
    setStatusMessage('');
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);

      const response = await api.post('/api/checkUserStatus', { email: user.email });
      const data = await response.json();

      setCurrentUser(user);      if (data.userState === 'verified' && data.docData?.fullData === true) {
        setUserDoc(data.docData);
        setUserStatus('verified');
        setLoginAttempts({ count: 0, lastAttempt: null });
        setIsLoading(false);
        return { status: 'verified', user, userDoc: data.docData };
      } else if (data.userState === 'pending' || data.userState === 'authNoDoc' || data.docData?.fullData === false) {
        setUserDoc(data.docData);
        // For email/password sign-in, only show pending_profile if the profile is actually incomplete
        // This prevents users with complete profiles from being stuck in profile completion
        if (data.docData?.fullData === true && user.emailVerified) {
          setUserStatus('verified');
          setLoginAttempts({ count: 0, lastAttempt: null });
          setIsLoading(false);
          return { status: 'verified', user, userDoc: data.docData };
        } else {
          const currentStatus = user.emailVerified ? 'pending_profile' : 'pending_verification';
          setUserStatus(currentStatus);
          setStatusMessage(user.emailVerified ? 'Please complete your profile.' : 'Please check your email to verify your account.');
          setShowResendButton(!user.emailVerified);
          setIsLoading(false);
          return { status: currentStatus, user, userDoc: data.docData };
        }
      } else {
        setUserDoc(null);
        setUserStatus('pending_profile');
        setStatusMessage('Error: User record inconsistent. Please complete profile.');
        setIsLoading(false);
        return { status: 'pending_profile', user, userDoc: null };
      }
    } catch (error) {
      const now = Date.now();
      setLoginAttempts(prev => ({
        count: prev.count + 1,
        lastAttempt: now
      }));      setIsLoading(false);

      // Check for specific Firebase auth errors
      if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
        setStatusMessage('Invalid email or password. Please check your credentials or register.');
          if (loginAttempts.count + 1 >= 2) {
           setShowResetButton(true);
         }
      } else if (error.code === 'auth/wrong-password') {
        setStatusMessage('Invalid password. Please try again.');
          if (loginAttempts.count + 1 >= 2) {
           setShowResetButton(true);
         }
      } else {
        setStatusMessage(`Sign-in failed: ${error.message}`);
        console.error('Sign-in error:', error);
      }
    }
  };

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && auth.currentUser) {
        try {
          await auth.currentUser.reload();
        } catch (e) {
          // Ignore reload errors
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoading(true);
      setStatusMessage('');
      setShowResendButton(false);
      setShowResetButton(false);

      if (!user) {
        setCurrentUser(null);
        setUserDoc(null);
        setUserStatus('unregistered');
        setIsLoading(false);
        return;
      }

      try {
        await user.getIdToken(true);
      } catch (refreshError) {
      }

      setCurrentUser(user);

      let serverStatusData;
      try {
        const response = await fetch('/api/checkUserStatus', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: user.email })
        });
        if (!response.ok) throw new Error(`Server status check failed: ${response.statusText}`);
        serverStatusData = await response.json();
      } catch (error) {
        console.error("Failed to check user status:", error);
        setUserDoc(null);
        setUserStatus('pending_verification');
        setStatusMessage('Error checking account status. Please try again later.');
        setIsLoading(false);
        return;
      }

      const { userState, docData } = serverStatusData;

      // Handle case where API returns nonExistant but user is authenticated (permission issue workaround)
      if (userState === 'nonExistant' && user && user.email) {
        // Try to create a pending user document if they're a new Google sign-in user
        if (user.providerData && user.providerData[0]?.providerId === 'google.com') {
          try {
            const displayName = user.displayName || '';
            const nameParts = displayName.split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            await setDoc(doc(db, 'pendingUsers', user.uid), {
              firstName,
              lastName,
              email: user.email.toLowerCase(),
              phone: '',
              grade: '',
              userType: '',
              subjects: [],
              createdAt: new Date(),
              emailVerified: user.emailVerified,
              fullData: false
            });

            setUserDoc({
              firstName,
              lastName,
              email: user.email,
              phone: '',
              grade: '',
              userType: '',
              subjects: [],
              emailVerified: user.emailVerified,
              fullData: false
            });
            setUserStatus('pending_profile');
            setStatusMessage('Please complete your profile information.');
          } catch (createError) {
            console.error('Error creating pendingUsers document:', createError);
            setUserStatus('pending_profile');
            setStatusMessage('Please complete your profile information.');
          }
        } else {
          // For email/password users, they should have a pending doc already
          setUserStatus(user.emailVerified ? 'pending_profile' : 'pending_verification');
          setStatusMessage(user.emailVerified ? 'Please complete your profile.' : 'Please verify your email.');
          setShowResendButton(!user.emailVerified);
        }
        setIsLoading(false);
        return;
      }

      if (user.emailVerified && userState === 'pending') {
        try {
          const verificationResponse = await api.post('/api/completeVerification', { email: user.email });
          const verificationData = await verificationResponse.json();

          if (verificationResponse.ok) {
            setUserDoc(verificationData.docData);
            if (verificationData.userState === 'verified') {
              setUserStatus('verified');
            } else {
              setUserStatus('pending_profile');
              setStatusMessage('Email verified! Please complete your profile.');
            }
          } else {
            setUserDoc(docData);
            setUserStatus('pending_verification');
            setStatusMessage(`Verification processing error: ${verificationData.message}`);
          }
        } catch (apiError) {
          console.error('Error calling completeVerification API:', apiError);
          setUserDoc(docData);
          setUserStatus('pending_verification');
          setStatusMessage('Error processing email verification. Please try refreshing.');
        }
      } else if (userState === 'verified' && docData?.fullData === true) {
        setUserDoc(docData);
        setUserStatus('verified');      
      } 
        else if (userState === 'pending' || userState === 'authNoDoc' || docData?.fullData === false) {
        setUserDoc(docData);
        if (!user.emailVerified) {
          setUserStatus('pending_verification');
          setStatusMessage('Please check your email to verify your account.');
          setShowResendButton(true);
        } else {
          // Check if user has complete profile data - if so, mark as verified
          if (docData?.fullData === true) {
            setUserStatus('verified');
          } else {
            setUserStatus('pending_profile');
            setStatusMessage('Please complete your profile information.');
          }
        }
      } else if (userState === 'nonExistant') {
        setUserDoc(null);
        setUserStatus('unregistered');
      } else {
        setUserDoc(null);
        setUserStatus('unregistered');
      }

      setIsLoading(false);
    });

    return () => {
      authUnsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  const value = {
    currentUser,
    userDoc,
    setUserDoc,
    userStatus,
    setUserStatus, // Add setUserStatus here
    isLoading,
    setIsLoading, // Add setIsLoading here
    statusMessage,
    showResendButton,
    showResetButton,
    setStatusMessage,
    setShowResendButton,
    setShowResetButton,
    signInWithGoogle,
    signOutUser,
    resendVerificationEmail,
    signInWithEmailPassword,
    loginAttempts,
    setLoginAttempts
  };

  if (isLoading && !currentUser) {
    return <div>Loading...</div>;
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}
